'use client';

import { useEffect } from 'react';

/**
 * ServiceWorkerRegistration Component
 * Registers the service worker for PWA functionality
 * Should be included once in the app root (via Providers)
 */
export function ServiceWorkerRegistration() {
  useEffect(() => {
    // Only register in browser environment
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    // Don't register service worker for Chrome extension routes
    // The service worker conflicts with Chrome extension context
    if (window.location.pathname.startsWith('/chrome-extension/')) {
      console.log('[PWA] Skipping service worker registration for Chrome extension route');
      return;
    }

    // Register service worker
    const registerServiceWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
          updateViaCache: 'none', // Always check for updates
        });

        console.log('[PWA] Service Worker registered successfully:', registration.scope);

        // Check for updates periodically (every 1 hour)
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);

        // Listen for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New service worker is available
              console.log('[PWA] New version available! Refresh to update.');
              
              // Optionally show a notification to the user
              // You could use a toast notification here
            }
          });
        });
      } catch (error) {
        console.error('[PWA] Service Worker registration failed:', error);
      }
    };

    // Register on mount
    registerServiceWorker();

    // Listen for service worker messages
    const messageHandler = (event: MessageEvent) => {
      console.log('[PWA] Message from service worker:', event.data);
      
      // Handle any messages from the service worker
      if (event.data?.type === 'CACHE_UPDATED') {
        console.log('[PWA] Cache updated');
      }
    };

    navigator.serviceWorker.addEventListener('message', messageHandler);

    // Cleanup
    return () => {
      navigator.serviceWorker.removeEventListener('message', messageHandler);
    };
  }, []);

  return null; // This component doesn't render anything
}
